import './styles/GoogleFontDropdown.scss';

import React, { useEffect, useMemo, useState } from 'react';

import { cn } from '../../../../helpers/classBuilders';
import {
  useClassHelper,
  useGoogleFonts,
  useOutsideClick,
} from '../../../../hooks';
import { fetchFonts } from './GoogleFontDropdown.utils';

const componentName = 'c-GoogleFontDropdown';

interface GoogleFontDropdownProps {
  blacklist?: string[];
  apiKey?: string;
  name: string;
  value?: string;
  placeholder?: string;
  onChange: (event: { name: string; value: string }) => void;
}

export const GoogleFontDropdown = ({
  blacklist = [],
  apiKey = 'AIzaSyC8srGUW1FakrdOZ9Dg-2Anf6jyhX4E8vg',
  name,
  onChange,
  value,
  placeholder = 'Select Font',
}: GoogleFontDropdownProps) => {
  const [fonts, setFonts] = useState<Array<{ family: string }>>([]);
  const [selectedFont, setSelectedFont] = useState<string>('');
  const [isOptionsListOpen, setIsOptionsListOpen] = useState<boolean>(false);
  const ref = React.createRef<HTMLDivElement>();
  const c = useClassHelper(componentName);
  useOutsideClick(ref, () => setIsOptionsListOpen(false));

  const toggle = () => {
    setIsOptionsListOpen(!isOptionsListOpen);
  };

  const currentFont = value || selectedFont;

  const fontsArray = useMemo(() => fonts.map(font => font.family), [fonts]);

  useGoogleFonts(fontsArray);

  useEffect(() => {
    const fetchGoogleFonts = async () => {
      const googleFonts = await fetchFonts(apiKey);

      const filteredFonts = googleFonts.items
        .filter(
          (font: { family: string }) =>
            ![...blacklist, 'icons', 'symbol'].some(blacklisted =>
              font.family
                .toLowerCase()
                .includes(blacklisted.toLocaleLowerCase()),
            ),
        )
        .slice(0, 100)
        .sort((a: { family: string }, b: { family: string }) =>
          a.family.localeCompare(b.family),
        );

      setFonts(filteredFonts);
    };

    fetchGoogleFonts();
  }, []);

  const handleFontChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    const target = event.target as HTMLButtonElement;
    const font = target.getAttribute('data-value');
    setSelectedFont(font);
    onChange({ name, value: font });
    setIsOptionsListOpen(false);
  };

  return (
    <div className={componentName} ref={ref}>
      <button
        type="button"
        onClick={toggle}
        className={`${c(
          '__select-trigger',
          isOptionsListOpen && '__select-trigger--open',
        )} ${cn('border border-gray-300 focus-visible:focus-ring-form', {
          'border-blue-700': isOptionsListOpen,
        })}`}
      >
        <p className={cn({ 'text-gray-500': !currentFont })}>
          {currentFont || placeholder}
        </p>
        <p className={c('__select-trigger--icon')}></p>
      </button>
      <ul
        className={`${c('__options-list')} ${cn({
          'border border-gray-300': isOptionsListOpen,
        })}`}
      >
        {fonts.map((font, index) => (
          <li key={index}>
            <button
              type="button"
              className={`${c(
                '__option-button',
                currentFont === font.family && '__option-button--selected',
              )} focus-visible:focus-ring-form-inset`}
              data-value={font.family}
              onClick={handleFontChange}
              style={{ fontFamily: font.family }}
            >
              {font.family}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
